<script setup lang="ts">
import { computedWithControl, useSwipe, useWindowSize } from "@vueuse/core";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import gsap from "gsap";
import { Mate } from "@/types/entity";

const props = defineProps<{
  initialTop?: number;
  maxWidth: number;
  maxHeight: number;
  pages?: { content: string; header?: string }[]; // 新增：页面内容数组
  mate: Mate;
}>();

const emit = defineEmits<{
  (e: "show-detail"): void;
}>();

const currentPage = ref(-1);
const initialTop = computed(() =>
  typeof props.initialTop === "number"
    ? `${props.initialTop + bookHeight.value / 2}px`
    : "50%"
);
const bookRef = ref<HTMLElement | null>(null);
// 内页的总页数
const pageLength = computed(() =>
  pageContents.value.length % 2 === 0
    ? pageContents.value.length
    : pageContents.value.length + 1
);
// 物理页数（正反面算作一页）
const pageCount = computed(() => Math.ceil(pageLength.value / 2));

const bookWidth = computed(() => props.maxWidth * 0.45);
const bookHeight = computed(() => (bookWidth.value * 4) / 3);
const spineTransZ = computed(() => `-${pageCount.value + 3}px`);

const { direction } = useSwipe(bookRef);

const changeCurrentPage = (mode: "next" | "prev") => {
  if (mode === "next") {
    currentPage.value = Math.min(currentPage.value + 1, pageCount.value);
  } else {
    currentPage.value = Math.max(currentPage.value - 1, -1);
  }
};

watch(direction, (direction) => {
  if (direction === "left") {
    changeCurrentPage("next");
  } else if (direction === "right") {
    changeCurrentPage("prev");
  }
});

const { width: windowWidth, height: windowHeight } = useWindowSize();
onMounted(() => {
  setTimeout(async () => {
    pageContents.trigger();
    // gsap.to(bookRef.value, {
    //   duration: 1,
    //   left: `${windowWidth.value * 0.5 + bookWidth.value / 2}px`,
    //   scale: 1,
    // });
    await nextTick();
    watch(
      [currentPage, windowWidth, windowHeight],
      ([currentPage]) => {
        // 获取所有页面元素
        const pages = document.querySelectorAll(".book__page");

        if (currentPage !== -1) {
          gsap.to(bookRef.value, {
            top: "50%",
            left: `${windowWidth.value * 0.5 + bookWidth.value / 2}px`,
            scale: 1,
          });
        } else {
          gsap.to(bookRef.value, {
            top: initialTop.value,
            left: `${windowWidth.value * 0.5}px`,
            scale: 0.6,
          });
        }

        pages.forEach((page, index) => {
          const pageElement = page as HTMLElement;
          // 如果当前页码大于等于页面索引，则翻页
          if (currentPage >= index) {
            gsap.to(pageElement, {
              duration: 1,
              rotateY: -180,
              z: index === 0 ? -(pageCount.value + 3) : index,
            });
          } else {
            // 否则恢复原状
            gsap.to(pageElement, {
              duration: 1,
              rotateY: 0,
              z: index === 0 ? pageCount.value + 3 : -index,
            });
          }
        });
      },
      {
        flush: "post",
        immediate: true,
      }
    );
  }, 1000);
});

// 计算实际页面内容
const pageContents = computedWithControl(
  () => props.pages,
  () => {
    if (!props.pages || props.pages.length === 0) {
      return Array(2).fill({ content: "" });
    }

    // 创建一个新数组来存储分页后的内容
    const paginatedContents: { content: string; header?: string }[] = [];

    // 处理每个原始页面内容
    for (const page of props.pages) {
      const replacedContent = page.content.replace(/\\n/g, "\n");
      const pages = calculatePagination(replacedContent, page.header);
      paginatedContents.push(...pages);
    }

    return paginatedContents;
  }
);

// 计算分页的函数
const calculatePagination = (content: string, header?: string) => {
  // 获取原始页面内容容器的样式
  const pageContent = document.querySelector(".page__content");
  if (!pageContent) return [{ content, header }];

  // 创建一个隐藏的容器来测量内容
  const container = document.createElement("div");

  // 复制原始容器的所有计算样式
  const originStyle = window.getComputedStyle(pageContent);
  const styleNames: string[] = Array.prototype.slice.apply(originStyle);
  styleNames.forEach((name) => {
    container.style.setProperty(name, originStyle.getPropertyValue(name));
  });

  // 设置必要的覆盖样式
  container.style.position = "fixed";
  container.style.zIndex = "-9999";
  container.style.top = "-9999px";
  container.style.minHeight = "auto";
  container.style.maxHeight = "auto";
  container.style.height = "auto";
  container.style.top = "0";
  container.style.bottom = "auto";

  document.body.appendChild(container);

  // 计算单页可用高度（使用原始高度）
  const pageHeight = Number(
    originStyle.getPropertyValue("height").replace("px", "")
  );

  // 分页结果
  const pages: { content: string; header?: string }[] = [];
  let remainingContent = `${header ? `${header}\n` : ""}${content}`;

  while (remainingContent.length > 0) {
    // 设置内容到容器
    container.innerText = remainingContent;

    // 如果内容高度小于页面高度，直接添加到结果中
    if (container.offsetHeight <= pageHeight) {
      pages.push({
        content:
          pages.length === 0 && header
            ? remainingContent.replace(header ?? "", "")
            : remainingContent,
        header: pages.length === 0 ? header : undefined,
      });
      break;
    }

    // 如果单个字符就超过页面高度，强制放入一个字符并继续
    container.innerText = remainingContent[0];
    if (container.offsetHeight > pageHeight) {
      pages.push({
        content:
          pages.length === 0 && header
            ? remainingContent[0].replace(header ?? "", "")
            : remainingContent[0],
        header: pages.length === 0 ? header : undefined,
      });
      remainingContent = remainingContent.slice(1);
      continue;
    }

    // 二分查找确定当前页可以容纳的字符数
    let left = 1; // 从1开始，因为我们知道至少一个字符是可以放入的
    let right = remainingContent.length;
    let lastValidLength = 1; // 默认至少放入一个字符

    while (left < right) {
      const mid = Math.floor((left + right) / 2);
      container.innerText = remainingContent.slice(0, mid);

      if (container.offsetHeight <= pageHeight) {
        lastValidLength = mid;
        left = mid + 1;
      } else {
        right = mid;
      }
    }
    // 添加当前页内容
    pages.push({
      content:
        pages.length === 0 && header
          ? remainingContent.slice(0, lastValidLength).replace(header ?? "", "")
          : remainingContent.slice(0, lastValidLength),
      header: pages.length === 0 ? header : undefined,
    });

    // 确保每次至少消耗一个字符
    if (lastValidLength === 0) {
      lastValidLength = 1;
    }

    // 更新剩余内容
    remainingContent = remainingContent.slice(lastValidLength);
  }

  // 清理临时容器
  document.body.removeChild(container);
  return pages;
};
</script>
<template>
  <div
    class="h-full w-full overflow-x-hidden book-scroller-container"
    :class="{
      'fixed inset-0 bg-modal-pattern z-[3]': currentPage !== -1,
    }"
  >
    <div
      class="absolute text-white top-5 right-5 text-base"
      v-if="currentPage !== -1"
      @click="currentPage = -1"
    >
      <i class="iconify mingcute--close-circle-fill" />
    </div>
    <div
      ref="bookRef"
      class="book"
      :class="{
        'border-4 border-[#8C0341] rounded-tr-xl rounded-br-xl':
          currentPage === -1,
      }"
      :style="{
        width: `${bookWidth}px`,
        height: `${bookHeight}px`,
      }"
      @click="changeCurrentPage('next')"
    >
      <!-- 书脊 -->
      <div class="book__spine"></div>

      <!-- 封面 -->
      <div
        class="page book__page book__cover book__cover--front border-2 border-[#8C0341]"
        :style="{ '--page-index': 1 }"
      >
        <div class="page__half page__half--front">
          <div class="decorative-lines"></div>
          <div class="corner-decoration corner-decoration--tl"></div>
          <div class="corner-decoration corner-decoration--tr"></div>
          <div class="corner-decoration corner-decoration--bl"></div>
          <div class="corner-decoration corner-decoration--br"></div>

          <!-- 星星装饰 -->
          <div class="star star--tl"></div>
          <div class="star star--tr"></div>
          <div class="star star--bl"></div>
          <div class="star star--br"></div>

          <!-- 弧线装饰 -->
          <div class="curved-line curved-line--tl"></div>
          <div class="curved-line curved-line--tr"></div>
          <div class="curved-line curved-line--bl"></div>
          <div class="curved-line curved-line--br"></div>

          <div class="center-emblem flex flex-col gap-2 items-center">
            <div class="center-emblem__avatar max-w-[40%]">
              <UserAvatar :mate="mate" />
            </div>
            <div class="center-emblem__name text-center">冒险手帐</div>
          </div>
        </div>
        <div class="page__half page__half--back">
          <div class="book__insert"></div>
        </div>
      </div>

      <!-- 内页 -->
      <div
        v-for="(p, index) in pageCount"
        :key="index"
        class="page book__page"
        :style="{ '--page-index': index + 2 }"
      >
        <div class="page__half page__half--front">
          <div class="page__content">
            <div class="page_content__header text-black/50">
              {{ pageContents[index * 2]?.header }}
            </div>
            <div class="page_content__text text-black/80">
              {{ pageContents[index * 2]?.content }}
            </div>
          </div>
          <div class="page__number">{{ index * 2 + 1 }}</div>
        </div>
        <div class="page__half page__half--back">
          <div class="page__content">
            <div class="page_content__header text-black/50">
              {{ pageContents[index * 2 + 1]?.header }}
            </div>
            <div class="page_content__text text-black/80">
              {{ pageContents[index * 2 + 1]?.content }}
            </div>
          </div>
          <div class="page__number">{{ index * 2 + 2 }}</div>
        </div>
      </div>

      <!-- 封底 -->
      <div
        class="page book__page book__cover book__cover--back"
        :style="{ '--page-index': pageLength + 2 }"
      >
        <div class="page__half page__half--front"></div>
        <div class="page__half page__half--back"></div>
        <!-- GSAP Logo -->
        <div class="book__insert"></div>
      </div>
    </div>
    <div
      v-if="currentPage !== -1"
      class="absolute left-1/2 -translate-x-1/2"
      :style="{
        bottom: '20%',
      }"
    >
      <button
        class="btn btn-primary btn-sm rounded"
        @click="emit('show-detail')"
      >
        查看详情
      </button>
    </div>
  </div>
</template>

<style lang="postcss">
.book-scroller-container {
  --page-count: v-bind(pageLength);
  --underline: hsla(0, 0%, 25%, 0.4);
  --spine: hsl(0, 0%, 0%);
  /* --cover: hsl(0, 0%, 10%); */
  --cover: var(--p);
  --insert: hsl(0, 0%, 85%);
  --page: hsl(0, 0%, 90%);
  --container-height: v-bind(maxHeight);
  --spine-trans-z: v-bind(spineTransZ);
  font-family: "Cubic", "Segoe Print", "Bradley Hand", cursive;
  --line-color: 46 98% 62%;
  --emboss-color: 339 87% 41%;

  margin: 0;
  padding: 0;

  transition: background-color 0.5s ease-in-out;

  &.bg-modal-pattern {
    background-color: rgba(0, 0, 0, 0.9);
    background-image: linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.05) 25%,
        transparent 25%,
        transparent 75%,
        rgba(255, 255, 255, 0.05) 75%,
        rgba(255, 255, 255, 0.05)
      ),
      linear-gradient(
        -45deg,
        rgba(255, 255, 255, 0.05) 25%,
        transparent 25%,
        transparent 75%,
        rgba(255, 255, 255, 0.05) 75%,
        rgba(255, 255, 255, 0.05)
      );
    background-size: 60px 60px;
    background-position: 0 0, 30px 30px;
    backdrop-filter: blur(2px);
  }

  .page {
    height: 100%;
    width: 100%;
    position: relative;
    transform-style: preserve-3d;

    .page__number {
      position: absolute;
      color: hsl(0, 0%, 50%);
      bottom: 1rem;
      font-size: 1vmin;
    }

    &__content {
      position: absolute;
      top: 0;
      left: 1rem;
      right: 1rem;
      bottom: 0;
      overflow: hidden;
      padding: 1rem 0 1rem 0;
      font-size: 0.7rem;
      line-height: 1.1rem;
      color: hsl(0, 0%, 20%);

      /* 隐藏滚动条但保持功能 */
      scrollbar-width: none;
      &::-webkit-scrollbar {
        display: none;
      }

      /* 确保内容在横线上方显示 */
      z-index: 1;
      background: radial-gradient(
            circle at center,
            var(--underline) 1px,
            transparent 1px
          )
          0 0 / 1rem 1rem,
        var(--page);
    }

    &_content__text {
      white-space: pre-wrap;
    }

    &__half {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      transform: rotateY(calc(var(--rotation) * 1deg))
        translate3d(0, 0, calc((0.5 * var(--coefficient)) * 1px));

      $clip: inset(0 0.5% 0 0.5%);
      -webkit-clip-path: $clip;
      clip-path: $clip;

      &--front {
        --rotation: 0;
        --coefficient: 0;
        backface-visibility: hidden;
        border-radius: 0 5% 5% 0;

        .page__number {
          right: 1rem;
        }

        .page__content {
          text-align: justify;
        }
      }

      &--back {
        --rotation: 180;
        --coefficient: 2;
        border-radius: 5% 0 0 5%;

        .page__number {
          left: 1rem;
        }

        .page__content {
          text-align: justify;
        }
      }
    }
  }

  .book {
    position: fixed;
    min-width: 150px;
    min-height: 200px;
    top: v-bind(initialTop);
    left: 50%;
    transform: translate(-50%, -50%) scale(0.6);
    transform-style: preserve-3d;
    perspective: 1200px;

    &__insert {
      content: "";
      position: absolute;
      height: 94%;
      width: 94%;
      background: var(--insert);
      top: 50%;
      right: -1rem;
      transform: translate(0, -50%);
      border-radius: 5% 0 0 5%;
    }

    &__spine {
      height: 100%;
      left: 0;
      top: 0;
      position: absolute;
      background: var(--spine);
      transform-origin: 0 50%;
      width: 12px;
      transform: translate3d(0, 0, var(--spine-trans-z)) scaleX(1) rotateY(0deg);
    }

    &__page {
      position: absolute;
      left: 1%;
      top: 50%;
      border-radius: 0 5% 5% 0;
      transform: translate(0, -50%);
      height: 94%;
      width: 94%;
      z-index: calc(((var(--page-count) + 2) - var(--page-index)) * 2);
      transform-origin: 0% 50%;
    }

    &__cover {
      border-radius: 0 5% 5% 0;
      height: 100%;
      width: 100%;
      position: absolute;
      top: 50%;
      left: 0;
      background: #fb4e78;
      position: absolute;

      &::before {
        content: "";
        position: absolute;
        inset: 0;
        background: repeating-linear-gradient(
              to right,
              transparent 0px,
              rgba(0, 0, 0, 0.1) 1px,
              transparent 2px
            )
            0 0 / 4px 100%,
          repeating-linear-gradient(
              to bottom,
              transparent 0px,
              rgba(0, 0, 0, 0.1) 1px,
              transparent 2px
            )
            0 0 / 100% 4px;
        pointer-events: none;
      }

      .decorative-lines {
        position: absolute;
        z-index: 2;
        inset: 40px;
        border: 4px solid hsl(var(--line-color));
        border-radius: 4px;

        /* &::before {
          content: "";
          position: absolute;
          inset: 15px;
          border: 1px solid rgba(218, 165, 32, 0.5);
          border-radius: 3px;
        }

        &::after {
          content: "";
          position: absolute;
          inset: 30px;
          border: 1px solid rgba(218, 165, 32, 0.5);
          border-radius: 2px;
        } */
      }

      .star {
        position: absolute;
        width: 10px;
        height: 10px;
        background: linear-gradient(
          45deg,
          hsl(var(--line-color)),
          hsl(var(--line-color))
        );
        clip-path: polygon(
          50% 0%,
          61% 35%,
          98% 35%,
          68% 57%,
          79% 91%,
          50% 70%,
          21% 91%,
          32% 57%,
          2% 35%,
          39% 35%
        );

        &--tl {
          top: 30px;
          left: 30px;
        }
        &--tr {
          top: 30px;
          right: 30px;
        }
        &--bl {
          bottom: 30px;
          left: 30px;
        }
        &--br {
          bottom: 30px;
          right: 30px;
        }
      }

      .curved-line {
        position: absolute;
        width: 60px;
        height: 40px;
        border: 4px solid transparent;
        border-top-color: hsl(var(--line-color) / 0.5);
        border-radius: 50%;
        transform-origin: 50% 100%;

        &--tl {
          top: 40px;
          left: 50px;
          transform: rotate(-45deg);
        }

        &--tr {
          top: 40px;
          right: 50px;
          transform: rotate(45deg);
        }

        &--bl {
          bottom: 80px;
          left: 50px;
          transform: rotate(225deg);
        }

        &--br {
          bottom: 80px;
          right: 50px;
          transform: rotate(135deg);
        }
      }

      .embossed-line {
        position: absolute;
        background: none;
        box-shadow: inset 1px 1px 1px hsl(var(--emboss-color)),
          inset -1px -1px 1px hsl(var(--emboss-color));

        &--h {
          height: 2px;
          left: 80px;
          right: 80px;
          &.top {
            top: 60px;
          }
          &.bottom {
            bottom: 60px;
          }
        }

        &--v {
          width: 2px;
          top: 80px;
          bottom: 80px;
          &.left {
            left: 60px;
          }
          &.right {
            right: 60px;
          }
        }
      }

      &::after {
        content: "";
        position: absolute;
        inset: 20px;
        border: 2px solid hsl(var(--emboss-color) / 0.5);
        background: linear-gradient(
              45deg,
              transparent 48%,
              hsl(var(--emboss-color)) 49%,
              hsl(var(--emboss-color)) 51%,
              transparent 52%
            )
            0 0 / 20px 20px,
          linear-gradient(
              -45deg,
              transparent 48%,
              hsl(var(--emboss-color)) 49%,
              hsl(var(--emboss-color)) 51%,
              transparent 52%
            )
            0 0 / 20px 20px;
        opacity: 0.5;
        z-index: -1;
      }

      .corner-decoration {
        position: absolute;
        width: 60px;
        height: 60px;
        border: 4px solid hsl(var(--line-color));

        &--tl {
          top: 20px;
          left: 20px;
          border-right: none;
          border-bottom: none;
        }

        &--tr {
          top: 20px;
          right: 20px;
          border-left: none;
          border-bottom: none;
        }

        &--bl {
          bottom: 20px;
          left: 20px;
          border-right: none;
          border-top: none;
        }

        &--br {
          bottom: 20px;
          right: 20px;
          border-left: none;
          border-top: none;
        }
      }

      .center-emblem {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);

        &__avatar {
          background: linear-gradient(
            45deg,
            hsl(var(--line-color)),
            hsl(var(--line-color))
          );
          width: 80px;
          @apply p-0.5 rounded;
        }

        &__name {
          color: hsl(var(--line-color));
        }
      }

      &--front {
        transform-origin: 0 50%;

        .page__half--back {
          border-right: 1rem solid var(--spine);
        }
      }

      &--back {
        transform-origin: 0% 50%;

        .page__half--front {
          border-left: 1rem solid var(--spine);
        }

        .book__insert {
          left: 0;
          border-radius: 0 5% 5% 0;
        }
      }
    }

    &__page:not(.book__cover) .page__half {
      background: radial-gradient(
            circle at center,
            var(--underline) 1px,
            transparent 1px
          )
          0 0 / 1rem 1rem,
        var(--page);
      overflow: hidden;
      align-items: start;
    }
  }
}
</style>
